import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, message, Alert } from 'antd';
import { LockOutlined, SafetyOutlined, KeyOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../services/api';

const { Title, Text } = Typography;

const AccountSettings = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    const { currentPassword, newPassword, confirmPassword } = values;

    if (newPassword !== confirmPassword) {
      message.error('La confirmation ne correspond pas au nouveau mot de passe');
      return;
    }

    setIsSubmitting(true);
    try {
      await authService.changePassword({ currentPassword, newPassword });
      message.success('Mot de passe modifié avec succès');
      form.resetFields();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0]?.msg ||
        'Erreur lors de la modification du mot de passe';
      message.error(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto 0 0' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, color: '#002c3e', fontWeight: 800 }}>
          Paramètres du compte
        </Title>
        <Text type="secondary">
          Modifiez le mot de passe de votre compte administrateur.
        </Text>
      </div>

      <Card
        title={
          <span style={{ color: '#002c3e' }}>
            <KeyOutlined style={{ marginRight: 8, color: '#10b981' }} />
            Modifier le mot de passe
          </span>
        }
        style={{ borderRadius: 12, borderTop: '3px solid #10b981' }}
      >


        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          requiredMark={false}
        >
          <Form.Item
            label="Mot de passe actuel"
            name="currentPassword"
            rules={[{ required: true, message: 'Le mot de passe actuel est requis' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Entrez votre mot de passe actuel"
              size="large"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item
            label="Nouveau mot de passe"
            name="newPassword"
            rules={[
              { required: true, message: 'Le nouveau mot de passe est requis' },
              { min: 6, message: 'Au moins 6 caractères' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                message: 'Doit contenir au moins une minuscule, une majuscule et un chiffre',
              },
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<KeyOutlined />}
              placeholder="Entrez le nouveau mot de passe"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            label="Confirmer le nouveau mot de passe"
            name="confirmPassword"
            dependencies={['newPassword']}
            hasFeedback
            rules={[
              { required: true, message: 'Veuillez confirmer le mot de passe' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<KeyOutlined />}
              placeholder="Retapez le nouveau mot de passe"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              size="large"
              icon={<SafetyOutlined />}
              style={{
                background: '#10b981',
                borderColor: '#10b981',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.28)',
                fontWeight: 600,
                borderRadius: 10,
              }}
            >
              Enregistrer le nouveau mot de passe
            </Button>
            <Button
              type="default"
              size="large"
              style={{ marginLeft: 12 }}
              onClick={() => form.resetFields()}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AccountSettings;
